import lodash from 'lodash';
import CONFIG from '@/config/template.js';
import Api from '@/script/api';
import Call from '@/script/call';
import Vue from 'vue';

/** 开发环境或 kushang / deli 站点允许取消「跳过已上架商品」 */
function allowUncheckSkipListedGoods() {
    if (process.env.NODE_ENV === 'development') return true;
    try {
        const name = Call.sendSync('systemEvent.getName');
        return name === 'kushang' || name === 'deli';
    } catch (e) {
        return false;
    }
}

/** 易可图水印模式固定 pos=7(左上)、keep=2(自动调整至主图) */
function syncWatermarkModeFields(wm) {
    if (!wm || typeof wm !== 'object') return wm;
    if (wm.watermark_mode === 2) {
        wm.canvas_mode = true;
        wm.pos = 7;
        wm.keep = 2;
    } else if (wm.watermark_mode === 1) {
        wm.canvas_mode = false;
    }
    return wm;
}

/** 始终从 template.js 取 UI 元数据，避免已保存模板覆盖说明文案、选项列表等 */
function applyConfigMeta(defaults, target) {
    if (!defaults || !target) return target;
    if (defaults.label !== undefined) target.label = defaults.label;
    if (defaults.key !== undefined) target.key = defaults.key;
    if (defaults.desc !== undefined) target.desc = defaults.desc;
    if (defaults.ques !== undefined) target.ques = defaults.ques;
    for (const k in defaults) {
        if (k === 'options' || k.endsWith('_options')) {
            target[k] = defaults[k];
        }
    }
    return target;
}

function mergeConfigItem(key, savedData) {
    const defaults = CONFIG[key];
    const merged = { ...defaults, ...(savedData || {}) };
    applyConfigMeta(defaults, merged);
    return merged;
}

/** 3:4 易可图水印固定 canvas_mode，不单独配置 pos/keep */
function syncSubWatermarkModeFields(wm) {
    if (!wm || typeof wm !== 'object') return wm;
    if (wm.sub_watermark_mode == null || wm.sub_watermark_mode === '') {
        wm.sub_watermark_mode = 2;
    } else {
        wm.sub_watermark_mode = Number(wm.sub_watermark_mode) || 2;
    }
    if (wm.sub_watermark_mode === 2) {
        wm.sub_canvas_mode = true;
    } else if (wm.sub_watermark_mode === 1) {
        wm.sub_canvas_mode = false;
    }
    return wm;
}

const state = {
    config: lodash.cloneDeep(CONFIG),
    oldConfig: {},  //记录之前数据，便于重置数据
    editid: 0,
};

const mutations = {
    SET_EDITID(state, id) {
        state.editid = id;
        // console.warn("当前编辑的配置id为：" + state.editid + "，请勿修改");
    },
    SET_CONFIG(state, data) {
        const result = {};
        for (let key in CONFIG) {
            // 处理新增的字段
            if (key == "white_background_image" && data[key] && typeof data[key].value == Boolean) {
                data[key].value = 0;
            }
            if (key === "main_image_watermark" && data[key]) {
                const raw = data[key];
                const merged = mergeConfigItem(key, raw);
                if (merged.canvas_mode === undefined) merged.canvas_mode = false;
                if (merged.watermark_mode == null) {
                    merged.watermark_mode = merged.canvas_mode ? 2 : 1;
                }
                syncWatermarkModeFields(merged);
                if (!Array.isArray(merged.water_mark_page_data)) merged.water_mark_page_data = [];
                if (!Array.isArray(merged.water_mark_source_images)) merged.water_mark_source_images = [];
                if (Array.isArray(merged.water_mark_images) && merged.water_mark_images.length > 1) {
                    merged.water_mark_images = merged.water_mark_images.slice(0, 1);
                    merged.water_mark_page_data = merged.water_mark_page_data.slice(0, 1);
                    merged.water_mark_source_images = merged.water_mark_source_images.slice(0, 1);
                }
                if (!('sub_water_mark_images' in raw)) {
                    merged.sub_watermark_use_main_fallback = true;
                    delete merged.sub_water_mark_images;
                    delete merged.sub_water_mark_page_data;
                } else {
                    merged.sub_watermark_use_main_fallback = false;
                    if (!Array.isArray(merged.sub_water_mark_page_data)) merged.sub_water_mark_page_data = [];
                    if (!Array.isArray(merged.sub_water_mark_source_images)) merged.sub_water_mark_source_images = [];
                    if (Array.isArray(merged.sub_water_mark_images) && merged.sub_water_mark_images.length > 1) {
                        merged.sub_water_mark_images = merged.sub_water_mark_images.slice(0, 1);
                        merged.sub_water_mark_page_data = merged.sub_water_mark_page_data.slice(0, 1);
                        merged.sub_water_mark_source_images = merged.sub_water_mark_source_images.slice(0, 1);
                    }
                }
                syncSubWatermarkModeFields(merged);
                if (!Array.isArray(merged.sub_water_mark_images)) {
                    merged.sub_water_mark_images = [];
                }
                if (!Array.isArray(merged.sub_water_mark_page_data)) {
                    merged.sub_water_mark_page_data = [];
                }
                if (!Array.isArray(merged.sub_water_mark_source_images)) {
                    merged.sub_water_mark_source_images = [];
                }
                result[key] = merged;
                continue;
            }
            result[key] = mergeConfigItem(key, data[key]);
            // 主图视频已下线「不上传 / 有则传无则不传」，旧值迁移到推荐项
            if (key === "main_video" && result[key]) {
                const mv = Number(result[key].value);
                if (mv === 1 || mv === 2 || !mv) {
                    result[key].value = 4;
                }
            }
            // 白底图必选：旧模板关闭/非法值迁移为默认第一项（免费转白底图）
            if (key === "white_background_image" && result[key]) {
                const wv = Number(result[key].value);
                if (wv !== 1 && wv !== 2) {
                    result[key].value = CONFIG.white_background_image.value;
                }
            }
            // 1688运费：旧 boolean 勾选 → 原样加(1)；未勾选 → 关闭(0)
            if (key === "add_1688_freight_to_sku" && result[key]) {
                const raw = result[key].value;
                if (raw === true) {
                    result[key].value = 1;
                } else if (raw === false || raw == null || raw === "") {
                    result[key].value = 0;
                } else {
                    const mode = Number(raw);
                    result[key].value = mode === 1 || mode === 2 ? mode : 0;
                }
                if (result[key].multiplication == null || result[key].multiplication === "") {
                    result[key].multiplication = 1;
                }
            }
            // 跳过已上架商品：正式环境强制开启；开发环境 / kushang / deli 站点允许关闭
            if (key === "skip_config" && result[key] && !allowUncheckSkipListedGoods()) {
                let vals = result[key].value;
                if (!Array.isArray(vals)) vals = [];
                vals = vals.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
                if (!vals.includes(1)) vals.unshift(1);
                result[key].value = vals;
            }
        }

        state.config = result;
        state.oldConfig = lodash.cloneDeep(result);
    },
    RESET_CONFIG_DATA(state) {
        state.config = lodash.cloneDeep(state.oldConfig);
    },
    UPDATE_WHITE_BACKGROUND_IMAGE(state, value) {
        state.config.white_background_image.value = value;
    },
    UPDATE_FIELD_VALUE(state, { path, value, type = 'reset' }) {
        const keys = path.split('.');

        let current = state;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (current?.[key]) {
                current = current[key];
            }
        }
        const lastKey = keys[keys.length - 1];
        if (type == "reset") {
            Vue.set(current, lastKey, value); // ✅ 关键点
        } else if (type == "add") {
            if (Array.isArray(current[lastKey])) {
                current[lastKey].push(value);
            }
        }
    }
};

const actions = {
    // 获取配置详情
    async getConfigDetail({ commit }, id) {
        commit("SET_EDITID", id);
        return new Promise((resolve) => {
            if (id > -1) {
                Api.post('/admintooltaobaoaccount/getgoodstemplatedetail', { id })
                    .then((res) => {
                        if (res.code == 1) {
                            if (res.info?.basic_config) {

                                commit('SET_CONFIG', JSON.parse(res.info.basic_config));
                                // 更新编辑id
                                commit("SET_EDITID", res.info.id);
                            }
                        }
                        resolve();
                    });
            } else {
                commit('SET_CONFIG', lodash.cloneDeep(CONFIG))
                resolve();
            }
        });
    },
    // 保存配置
    async saveConfig({ state }, args) {
        let body = {};
        if (args?.id || state?.editid) {
            body = { id: args.id || state.editid };
        }
        if (args?.t_name) {
            body.t_name = args.t_name;
        }
        if (args?.is_default) {
            body.is_default = args.is_default;
        }
        if (args?.basic_config) {
            const config = lodash.cloneDeep(state.config);
            syncWatermarkModeFields(config.main_image_watermark);
            syncSubWatermarkModeFields(config.main_image_watermark);
            body.basic_config = JSON.stringify(config);
        }
        return new Promise((resolve, reject) => {
            Api.post('/admintooltaobaoaccount/addgoodstemplate', body)
                .then((res) => {
                    if (body?.id) {
                        window.vue.$call.invoke("douDianNewPublishV2.delTemplate", body.id);
                    }
                    resolve(res);
                }).catch((err) => {
                    reject(err);
                });
        });
    },
    // 将当前模板指定字段同步到其他模板
    async syncConfigFieldToTemplates({ state }, { keyName, fieldKey, templateIds }) {
        const sourceValue = lodash.cloneDeep(state.config?.[keyName]?.[fieldKey]);
        if (sourceValue === undefined) {
            throw new Error('同步数据不存在');
        }
        const result = { success: 0, fail: 0 };
        for (const rawId of templateIds || []) {
            const id = Number(rawId);
            if (!id) {
                result.fail++;
                continue;
            }
            try {
                const detailRes = await Api.post('/admintooltaobaoaccount/getgoodstemplatedetail', { id });
                if (detailRes.code != 1 || !detailRes.info?.basic_config) {
                    result.fail++;
                    continue;
                }
                const config = JSON.parse(detailRes.info.basic_config);
                if (!config[keyName]) {
                    config[keyName] = lodash.cloneDeep(CONFIG[keyName] || {});
                }
                config[keyName][fieldKey] = lodash.cloneDeep(sourceValue);
                const saveRes = await Api.post('/admintooltaobaoaccount/addgoodstemplate', {
                    id,
                    basic_config: JSON.stringify(config),
                });
                if (saveRes.code == 1) {
                    result.success++;
                    window.vue.$call.invoke('douDianNewPublishV2.delTemplate', id);
                } else {
                    result.fail++;
                }
            } catch (e) {
                result.fail++;
            }
        }
        return result;
    },
    // 删除模板
    async deleteConfig({ commit }, id) {
        return new Promise((resolve, reject) => {
            Api.post('/admintooltaobaoaccount/deletegoodstemplate', { id })
                .then((res) => {
                    resolve(res);
                }).catch((err) => {
                    reject(err);
                });
        });
    },

    checkFormData({ state }) {
        const form = state.config;
        const result = {
            success: false,
            message: ""
        }
        if (form.classification.value == 2 && !form.classification?.sub_value?.cid) {
            result.message = "请先手动选择一个类目";
            return result;
        }

        if (form.brand_setting.value == 3 && !form.brand_setting.sub_value) {
            result.message = "请输入要指定的品牌名称";
            return result;
        }

        if (form.main_image_watermark.value && form.main_image_watermark.water_mark_images.length == 0) {
            result.message = "请先上传一张水印图片";
            return result;
        }

        if (form.main_image_watermark.sub_value) {
            const wm = form.main_image_watermark;
            const hasSubWatermark = wm.sub_water_mark_images?.length > 0;
            if (!hasSubWatermark) {
                if (wm.sub_watermark_use_main_fallback && wm.water_mark_images?.length > 0) {
                    // 旧模板迁移：继续用主图水印
                } else {
                    result.message = "请先上传3:4专用水印图";
                    return result;
                }
            }
        }

        if (form.add_description.first && form.add_description.first_image.length == 0) {
            result.message = "请先上传首部添加描述图";
            return result;
        }

        if (form.add_description.after && form.add_description.after_image.length == 0) {
            result.message = "请先上传尾部添加描述图";
            return result;
        }

        if ((form.sku_low_price_hide?.avg_value || form.sku_low_price_hide?.max_value || form.sku_low_price_hide?.min_value) && form.sku_low_price?.value) {
            // 互斥兜底：优先保留「低价SKU隐藏」
            form.sku_low_price.value = false;
        }

        if (form.sku_low_price.value && form.sku_low_price.sub_value == 2 && !form.sku_low_price.add_sku.name) {
            result.message = "请输入要添加的SKU名称";
            return result;
        }

        const whiteBg = Number(form.white_background_image?.value);
        if (whiteBg !== 1 && whiteBg !== 2) {
            result.message = "请选择白底图生成方式";
            return result;
        }

        result.success = true;
        return result;
    }
};

const getters = {
    get_config: (state) => state.config,
};

export default {
    namespaced: true, // 推荐开启命名空间
    state,
    mutations,
    actions,
    getters,
};
